// public/js/features/pois/poi-data.js
// Single entry point for getting POI data into poiState, through the cache.
//
//   ensurePoiData()              → cache-first: serve storage, revalidate in the
//                                  background once it is older than 10 min
//   ensurePoiData({ force:true}) → bypass cache (after create/edit/delete, or the
//                                  "refresh map data" button) and refresh storage
//   primePoiData(types, pois)    → feed a payload somebody else just fetched into
//                                  the cache + state, instead of throwing it away
import { loadPoiData } from './api.js';
import { poiState } from './poi-state.js';
import { readPoiCache, writePoiCache } from './poi-cache.js';

let inFlight = null;

export async function ensurePoiData({ force = false } = {}) {
  if (!force) {
    const cached = readPoiCache();
    if (cached && cached.fresh) {
      poiState.setData(cached.types, cached.pois);
      // Serve now, refresh behind it. setData emits, so the type list and the
      // map re-render by themselves when the fresh payload lands.
      if (cached.stale) revalidate();
      return { fromCache: true, revalidating: !!cached.stale };
    }
  }
  await revalidate();
  return { fromCache: false };
}

// One fetch at a time: the map page, the POI nav and a modal can all ask at once.
function revalidate() {
  if (!inFlight) {
    inFlight = loadPoiData()
      .then(d => { primePoiData(d.types, d.pois); })
      .catch(e => { console.warn('[poi-data] refresh failed', e); })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/** Store a payload that was just fetched elsewhere (the settings pages do their
 *  own uncached load - no reason to leave the cache behind stale). */
export function primePoiData(types = [], pois = []) {
  writePoiCache(types, pois);
  poiState.setData(types, pois);
}

export const refreshPoiData = () => ensurePoiData({ force: true });
